'use client'

import ConfirmModal from '@/components/confirm-modal'
import { ProfessionalSearchSelector } from '@/components/ProfessionalSearchSelector'
import {
    CONSENT_TYPE_LABELS,
    ConsentType,
    createConsent,
    CreateConsentData,
    deleteConsent,
    getConsentsByClient,
    getConsentTemplates,
    InformedConsent,
    InformedConsentTemplate,
    revokeConsent,
    sendConsentSigningEmail,
    updateConsent,
    UpdateConsentData,
} from '@/lib/api/prontuario/prontuario-api'
import { SocketContext } from '@/lib/providers/socket-provider'
import { useAuth } from '@/store/hooks'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useContext, useEffect, useState } from 'react'
import {
    MdAdd,
    MdCancel,
    MdCheck,
    MdClose,
    MdDelete,
    MdEdit,
    MdEmail,
    MdHandshake,
    MdPerson,
    MdVisibility,
} from 'react-icons/md'
import { toast } from 'react-toastify'
import { useProntuarioData } from './prontuario-context'

interface ConsentFormData {
    professionalId: string
    templateId: number | null
    type: ConsentType
    content: string
}

const defaultFormData: ConsentFormData = {
    professionalId: '',
    templateId: null,
    type: ConsentType.GENERAL_TREATMENT,
    content: '',
}

export default function ConsentimentosPage() {
    const { client, clientId } = useProntuarioData()
    const { user, isProfessional } = useAuth()
    const { socket } = useContext(SocketContext)

    const [consents, setConsents] = useState<InformedConsent[]>([])
    const [templates, setTemplates] = useState<InformedConsentTemplate[]>([])
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [sendingEmail, setSendingEmail] = useState<number | null>(null)
    const [revoking, setRevoking] = useState<number | null>(null)
    const [modalOpen, setModalOpen] = useState(false)
    const [detailModalOpen, setDetailModalOpen] = useState(false)
    const [revokeModalOpen, setRevokeModalOpen] = useState(false)
    const [editingId, setEditingId] = useState<number | null>(null)
    const [formData, setFormData] = useState<ConsentFormData>(defaultFormData)
    const [selectedConsent, setSelectedConsent] = useState<InformedConsent | null>(null)
    const [revokeReason, setRevokeReason] = useState('')
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [consentToDelete, setConsentToDelete] = useState<number | null>(null)

    useEffect(() => {
        if (clientId) {
            loadData()
        }
    }, [clientId])

    // Socket listener para atualização em tempo real
    useEffect(() => {
        if (!socket || !user?.companyId || !clientId) return

        const eventName = `consent-signed-${user.companyId}`
        const handler = (data: any) => {
            if (data.clientId === parseInt(clientId, 10)) {
                loadData()
                toast.info('Consentimento assinado pelo paciente!')
            }
        }

        socket.on(eventName, handler)
        return () => {
            socket.off(eventName, handler)
        }
    }, [socket, user?.companyId, clientId])

    const loadData = async () => {
        if (!clientId) return

        setLoading(true)
        try {
            const [consentsResponse, templatesResponse] = await Promise.all([
                getConsentsByClient(parseInt(clientId, 10)),
                user?.companyId ? getConsentTemplates(user.companyId) : Promise.resolve({ success: true, result: [] }),
            ])

            if (consentsResponse.success) {
                setConsents(consentsResponse.result || [])
            } else {
                toast.error(consentsResponse.error || 'Erro ao carregar consentimentos')
            }

            if (templatesResponse.success) {
                setTemplates(templatesResponse.result || [])
            }
        } catch (error) {
            toast.error('Erro ao carregar dados')
        } finally {
            setLoading(false)
        }
    }

    const openModal = (consent?: InformedConsent) => {
        if (consent) {
            setEditingId(consent.id)
            setFormData({
                professionalId: consent.professional?.id?.toString() || consent.professionalId?.toString() || '',
                templateId: consent.templateId || null,
                type: consent.type,
                content: consent.content,
            })
        } else {
            setEditingId(null)
            setFormData(defaultFormData)
        }
        setModalOpen(true)
    }

    const closeModal = () => {
        setModalOpen(false)
        setEditingId(null)
        setFormData(defaultFormData)
    }

    const openDetailModal = (consent: InformedConsent) => {
        setSelectedConsent(consent)
        setDetailModalOpen(true)
    }

    const closeDetailModal = () => {
        setDetailModalOpen(false)
        setSelectedConsent(null)
    }

    const openRevokeModal = (consent: InformedConsent) => {
        setSelectedConsent(consent)
        setRevokeReason('')
        setRevokeModalOpen(true)
    }

    const closeRevokeModal = () => {
        setRevokeModalOpen(false)
        setSelectedConsent(null)
        setRevokeReason('')
    }

    const handleTemplateChange = (templateId: number | null) => {
        const template = templates.find(t => t.id === templateId)
        setFormData(prev => ({
            ...prev,
            templateId,
            type: template?.type || prev.type,
            content: template?.content || prev.content,
        }))
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!isProfessional && !formData.professionalId) {
            toast.error('Selecione um profissional responsável')
            return
        }

        if (!formData.content.trim()) {
            toast.error('Informe o conteúdo do consentimento')
            return
        }

        if (!clientId) return

        setSaving(true)
        try {
            if (editingId) {
                const updateData: UpdateConsentData = {
                    templateId: formData.templateId || undefined,
                    type: formData.type,
                    content: formData.content.trim(),
                }

                const response = await updateConsent(editingId, updateData)
                if (response.success) {
                    toast.success('Consentimento atualizado com sucesso!')
                    await loadData()
                    closeModal()
                } else {
                    toast.error(response.error || 'Erro ao atualizar consentimento')
                }
            } else {
                const createData: CreateConsentData = {
                    clientId: parseInt(clientId, 10),
                    companyId: user!.companyId!,
                    professionalId: isProfessional ? user!.professionalId! : parseInt(formData.professionalId, 10),
                    templateId: formData.templateId || undefined,
                    type: formData.type,
                    content: formData.content.trim(),
                }

                const response = await createConsent(createData)
                if (response.success) {
                    toast.success('Consentimento criado com sucesso!')
                    await loadData()
                    closeModal()
                } else {
                    toast.error(response.error || 'Erro ao criar consentimento')
                }
            }
        } catch (error) {
            toast.error('Erro ao salvar consentimento')
        } finally {
            setSaving(false)
        }
    }

    const handleSendSigningEmail = async (consentId: number) => {
        setSendingEmail(consentId)
        try {
            const response = await sendConsentSigningEmail(consentId)
            if (response.success) {
                toast.success('Email de assinatura enviado com sucesso!')
            } else {
                toast.error(response.error || 'Erro ao enviar email')
            }
        } catch (error) {
            toast.error('Erro ao enviar email de assinatura')
        } finally {
            setSendingEmail(null)
        }
    }

    const handleRevoke = async () => {
        if (!selectedConsent) return

        if (!revokeReason.trim()) {
            toast.error('Informe o motivo da revogação')
            return
        }

        setRevoking(selectedConsent.id)
        try {
            const response = await revokeConsent(selectedConsent.id, revokeReason.trim())
            if (response.success) {
                toast.success('Consentimento revogado com sucesso!')
                await loadData()
                closeRevokeModal()
            } else {
                toast.error(response.error || 'Erro ao revogar consentimento')
            }
        } catch (error) {
            toast.error('Erro ao revogar consentimento')
        } finally {
            setRevoking(null)
        }
    }

    const handleDelete = (id: number) => {
        setConsentToDelete(id)
        setShowDeleteModal(true)
    }

    const confirmDelete = async () => {
        if (!consentToDelete) return

        try {
            const response = await deleteConsent(consentToDelete)
            if (response.success) {
                toast.success('Consentimento excluído com sucesso!')
                await loadData()
            } else {
                toast.error(response.error || 'Erro ao excluir consentimento')
            }
        } catch (error) {
            toast.error('Erro ao excluir consentimento')
        } finally {
            setShowDeleteModal(false)
            setConsentToDelete(null)
        }
    }

    const getStatusBadge = (consent: InformedConsent) => {
        if (consent.revokedAt) {
            return <span className="badge badge-error">Revogado</span>
        }
        if (consent.patientSignedAt) {
            return <span className="badge badge-success">Assinado</span>
        }
        return <span className="badge badge-warning">Pendente Assinatura</span>
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[300px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">Carregando consentimentos...</p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-bold text-base-content">Termos de Consentimento</h2>
                    <p className="text-sm text-base-content/60">
                        Gerencie os termos de consentimento informado
                    </p>
                </div>
                <button onClick={() => openModal()} className="btn btn-secondary gap-2">
                    <MdAdd className="text-lg" />
                    Novo Consentimento
                </button>
            </div>

            {/* Consents List */}
            {consents.length === 0 ? (
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-8 text-center">
                    <MdHandshake className="text-4xl mx-auto mb-3 text-base-content/20" />
                    <p className="text-base-content/60">Nenhum consentimento registrado</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {consents.map(consent => (
                        <div
                            key={consent.id}
                            className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-sm overflow-hidden"
                        >
                            <div className="p-5">
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-3">
                                            <div className="p-2 bg-secondary/10 rounded-lg">
                                                <MdHandshake className="text-xl text-secondary" />
                                            </div>
                                            <div>
                                                <h3 className="font-bold text-base-content">
                                                    {CONSENT_TYPE_LABELS[consent.type]}
                                                </h3>
                                                <p className="text-sm text-base-content/60">
                                                    Criado em: {format(new Date(consent.createdAt), 'dd/MM/yyyy', { locale: ptBR })}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="flex flex-wrap gap-2 mb-3">
                                            {getStatusBadge(consent)}
                                            {consent.template && (
                                                <span className="badge badge-ghost">
                                                    Template: {consent.template.name}
                                                </span>
                                            )}
                                        </div>

                                        <p className="text-sm text-base-content/70 line-clamp-2 mb-3">
                                            {consent.content}
                                        </p>

                                        {consent.patientSignedAt && (
                                            <p className="text-xs text-success">
                                                Assinado pelo paciente em: {format(new Date(consent.patientSignedAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                                            </p>
                                        )}

                                        {consent.guardianSignedAt && (
                                            <p className="text-xs text-success">
                                                Assinado pelo responsável ({consent.guardianName}) em: {format(new Date(consent.guardianSignedAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                                            </p>
                                        )}

                                        {consent.revokedAt && (
                                            <p className="text-xs text-error">
                                                Revogado em: {format(new Date(consent.revokedAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                                                {consent.revocationReason && ` - Motivo: ${consent.revocationReason}`}
                                            </p>
                                        )}

                                        {consent.professional && (
                                            <div className="flex items-center gap-2 text-xs text-base-content/50 mt-3">
                                                <MdPerson className="text-sm" />
                                                <span>Profissional: {consent.professional.name}</span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex flex-col items-end gap-2">
                                        <div className="flex items-center gap-1">
                                            <button
                                                onClick={() => openDetailModal(consent)}
                                                className="btn btn-ghost btn-sm btn-square"
                                                title="Detalhes"
                                            >
                                                <MdVisibility className="text-lg" />
                                            </button>
                                            {!consent.patientSignedAt && !consent.revokedAt && (
                                                <>
                                                    <button
                                                        onClick={() => openModal(consent)}
                                                        className="btn btn-ghost btn-sm btn-square"
                                                        title="Editar"
                                                    >
                                                        <MdEdit className="text-lg" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(consent.id)}
                                                        className="btn btn-ghost btn-sm btn-square text-error"
                                                        title="Excluir"
                                                    >
                                                        <MdDelete className="text-lg" />
                                                    </button>
                                                </>
                                            )}
                                        </div>

                                        {!consent.patientSignedAt && !consent.revokedAt && (
                                            <button
                                                onClick={() => handleSendSigningEmail(consent.id)}
                                                className="btn btn-info btn-sm gap-1"
                                                disabled={sendingEmail === consent.id}
                                                title="Enviar link de assinatura por email"
                                            >
                                                {sendingEmail === consent.id ? (
                                                    <span className="loading loading-spinner loading-sm"></span>
                                                ) : (
                                                    <MdEmail className="text-lg" />
                                                )}
                                                Enviar para Assinatura
                                            </button>
                                        )}

                                        {consent.patientSignedAt && !consent.revokedAt && (
                                            <button
                                                onClick={() => openRevokeModal(consent)}
                                                className="btn btn-error btn-outline btn-sm gap-1"
                                            >
                                                <MdCancel className="text-lg" />
                                                Revogar
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create/Edit Modal */}
            {modalOpen && (
                <div className="modal modal-open">
                    <div className="modal-box !max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-lg font-bold">
                                {editingId ? 'Editar Consentimento' : 'Novo Consentimento'}
                            </h3>
                            <button onClick={closeModal} className="btn btn-ghost btn-sm btn-square">
                                <MdClose className="text-xl" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            {!isProfessional && (
                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">Profissional Responsável <span className="text-error">*</span></span>
                                    </label>
                                    <ProfessionalSearchSelector
                                        value={formData.professionalId ? Number(formData.professionalId) : null}
                                        onChange={(id) => setFormData(prev => ({ ...prev, professionalId: id ? String(id) : '' }))}
                                        label=""
                                        placeholder="Selecione..."
                                        disabled={!!editingId}
                                    />
                                </div>
                            )}

                            {templates.length > 0 && (
                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">Template</span>
                                    </label>
                                    <select
                                        className="select select-bordered w-full"
                                        value={formData.templateId || ''}
                                        onChange={e => handleTemplateChange(e.target.value ? parseInt(e.target.value, 10) : null)}
                                    >
                                        <option value="">Sem template</option>
                                        {templates.map(template => (
                                            <option key={template.id} value={template.id}>
                                                {template.name} ({CONSENT_TYPE_LABELS[template.type]})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">Tipo de Consentimento <span className="text-error">*</span></span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={formData.type}
                                    onChange={e => setFormData(prev => ({ ...prev, type: e.target.value as ConsentType }))}
                                >
                                    {Object.entries(CONSENT_TYPE_LABELS).map(([key, label]) => (
                                        <option key={key} value={key}>{label}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">Conteúdo do Termo <span className="text-error">*</span></span>
                                </label>
                                <textarea
                                    className="textarea textarea-bordered w-full"
                                    placeholder="Texto do termo de consentimento..."
                                    rows={10}
                                    value={formData.content}
                                    onChange={e => setFormData(prev => ({ ...prev, content: e.target.value }))}
                                    required
                                />
                            </div>

                            <div className="modal-action">
                                <button type="button" onClick={closeModal} className="btn btn-ghost" disabled={saving}>
                                    Cancelar
                                </button>
                                <button type="submit" className="btn btn-secondary gap-2" disabled={saving}>
                                    {saving ? (
                                        <>
                                            <span className="loading loading-spinner loading-sm"></span>
                                            Salvando...
                                        </>
                                    ) : (
                                        <>
                                            <MdCheck className="text-lg" />
                                            Salvar
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                    <div className="modal-backdrop" onClick={closeModal}></div>
                </div>
            )}

            {/* Detail Modal */}
            {detailModalOpen && selectedConsent && (
                <div className="modal modal-open">
                    <div className="modal-box !max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-lg font-bold">Detalhes do Consentimento</h3>
                            <button onClick={closeDetailModal} className="btn btn-ghost btn-sm btn-square">
                                <MdClose className="text-xl" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div className="flex flex-wrap gap-2">
                                {getStatusBadge(selectedConsent)}
                            </div>

                            <div>
                                <h4 className="font-semibold text-base-content mb-1">Tipo</h4>
                                <p className="text-base-content/70">{CONSENT_TYPE_LABELS[selectedConsent.type]}</p>
                            </div>

                            <div>
                                <h4 className="font-semibold text-base-content mb-1">Conteúdo</h4>
                                <div className="p-4 bg-base-200 rounded-lg">
                                    <p className="text-base-content/70 whitespace-pre-wrap">{selectedConsent.content}</p>
                                </div>
                            </div>

                            {selectedConsent.patientSignedAt && (
                                <div>
                                    <h4 className="font-semibold text-base-content mb-1">Assinatura do Paciente</h4>
                                    <p className="text-base-content/70">
                                        {format(new Date(selectedConsent.patientSignedAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                                    </p>
                                    {selectedConsent.patientSignatureData?.image && (
                                        <div className="mt-2 p-2 bg-white rounded-lg border border-base-300 inline-block">
                                            <img
                                                src={selectedConsent.patientSignatureData.image}
                                                alt="Assinatura do paciente"
                                                className="max-h-24"
                                            />
                                        </div>
                                    )}
                                </div>
                            )}

                            {selectedConsent.guardianName && (
                                <div>
                                    <h4 className="font-semibold text-base-content mb-1">Responsável</h4>
                                    <p className="text-base-content/70">
                                        {selectedConsent.guardianName}
                                        {selectedConsent.guardianRelation && ` (${selectedConsent.guardianRelation})`}
                                    </p>
                                    {selectedConsent.guardianSignedAt && (
                                        <p className="text-sm text-success">
                                            Assinado em: {format(new Date(selectedConsent.guardianSignedAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                                        </p>
                                    )}
                                </div>
                            )}

                            {selectedConsent.revokedAt && (
                                <div className="p-4 bg-error/10 rounded-lg">
                                    <h4 className="font-semibold text-error mb-1">Consentimento Revogado</h4>
                                    <p className="text-error/70">
                                        Data: {format(new Date(selectedConsent.revokedAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                                    </p>
                                    {selectedConsent.revocationReason && (
                                        <p className="text-error/70">Motivo: {selectedConsent.revocationReason}</p>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="modal-action">
                            <button onClick={closeDetailModal} className="btn btn-ghost">Fechar</button>
                        </div>
                    </div>
                    <div className="modal-backdrop" onClick={closeDetailModal}></div>
                </div>
            )}

            {/* Revoke Modal */}
            {revokeModalOpen && selectedConsent && (
                <div className="modal modal-open">
                    <div className="modal-box">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-lg font-bold text-error">Revogar Consentimento</h3>
                            <button onClick={closeRevokeModal} className="btn btn-ghost btn-sm btn-square">
                                <MdClose className="text-xl" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <p className="text-base-content/70">
                                Você está prestes a revogar o consentimento de "{CONSENT_TYPE_LABELS[selectedConsent.type]}".
                                Esta ação não pode ser desfeita.
                            </p>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">Motivo da Revogação <span className="text-error">*</span></span>
                                </label>
                                <textarea
                                    className="textarea textarea-bordered w-full"
                                    placeholder="Informe o motivo da revogação..."
                                    rows={3}
                                    value={revokeReason}
                                    onChange={e => setRevokeReason(e.target.value)}
                                    required
                                />
                            </div>
                        </div>

                        <div className="modal-action">
                            <button onClick={closeRevokeModal} className="btn btn-ghost" disabled={revoking !== null}>
                                Cancelar
                            </button>
                            <button
                                onClick={handleRevoke}
                                className="btn btn-error gap-2"
                                disabled={revoking !== null || !revokeReason.trim()}
                            >
                                {revoking ? (
                                    <>
                                        <span className="loading loading-spinner loading-sm"></span>
                                        Revogando...
                                    </>
                                ) : (
                                    <>
                                        <MdCancel className="text-lg" />
                                        Confirmar Revogação
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                    <div className="modal-backdrop" onClick={closeRevokeModal}></div>
                </div>
            )}

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false)
                    setConsentToDelete(null)
                }}
                onConfirm={confirmDelete}
                title="Excluir consentimento"
                message="Tem certeza que deseja excluir este consentimento?"
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
            />
        </div>
    )
}
